import type { ReactNode } from "react";

import { cn } from "@/utils/utils";

/**
 * How a tooltip looks, without where it sits.
 *
 * Shared so the hover hint and the clipped-text preview are one thing with two
 * ways of being placed — the hint is a CSS sibling of its trigger, the preview
 * is a single box the whole document borrows. Two spellings of the same black
 * box is how they drift apart.
 */
export const TOOLTIP_SURFACE = cn(
  "text-footnote pointer-events-none z-50",
  // Wide enough for a hint, bounded so a sentence wraps instead of running off
  // the edge of the screen.
  "w-max max-w-64 text-center text-pretty",
  "rounded-md px-2 py-1 font-medium",
  "bg-ink-900 dark:bg-ink-100 dark:text-ink-900 text-white",
);

/**
 * A label that appears on hover and on keyboard focus.
 *
 * CSS only, and it never carries anything you cannot get another way: a
 * tooltip is unreachable on a touch screen, so it explains, it does not inform.
 * The text is wired in through `aria-describedby`'s cheaper cousin — a live
 * region is overkill for a hint, so the trigger keeps its own accessible name.
 */
export function Tooltip({
  label,
  side = "top",
  align = "center",
  children,
  className,
}: {
  /** Nothing to say means no tooltip at all, rather than an empty box. */
  label: ReactNode;
  side?: "top" | "bottom";
  /**
   * Which edge the box lines up with.
   *
   * Centred on the trigger by default, which is right until the trigger is
   * near the edge of the window: a centred box on a control against the right
   * margin puts half of itself off the screen, and nothing clips it back — it
   * is `fixed`-like `absolute` inside a bar that does not scroll. `end` hangs
   * it from the trigger's right edge instead, `start` from the left.
   */
  align?: "center" | "start" | "end";
  children: ReactNode;
  className?: string;
}) {
  if (!label) return <>{children}</>;
  return (
    <span className={cn("group/tip relative inline-flex", className)}>
      {children}
      <span
        role="tooltip"
        className={cn(
          TOOLTIP_SURFACE,
          "absolute",
          align === "center"
            ? "left-1/2 -translate-x-1/2"
            : align === "end"
              ? "right-0"
              : "left-0",
          "opacity-0 transition-opacity duration-[var(--duration-fast)]",
          /**
           * Hover, and keyboard focus — not focus of any kind.
           *
           * A native `<dialog>` hands focus back to whatever opened it when it
           * closes, so a tooltip on a button that opens one came back with the
           * focus and sat there over the page, nowhere near the pointer, until
           * something else was clicked. `:focus-visible` is the difference:
           * focus restored after a click does not match it, focus a person
           * tabbed to does, and the hint still answers the keyboard.
           */
          "group-hover/tip:opacity-100 group-has-[:focus-visible]/tip:opacity-100",
          side === "top" ? "bottom-full mb-1.5" : "top-full mt-1.5",
        )}
      >
        {label}
      </span>
    </span>
  );
}
